/**
 * Phase 18 — Enterprise Mock Invoice Service Layer
 * In-memory relational store with 510+ realistic invoice records, 3-way matching engine,
 * duplicate screening, exception lifecycle, and simulated SAP S/4HANA MIRO integration.
 */

import {
  InvoiceRecord,
  InvoiceItemRecord,
  InvoiceMetricsSummary,
  InvoiceFilterState,
  CreateInvoiceInput,
  InvoiceException,
} from '@/types/domain/invoice-management';
import { MOCK_PURCHASE_ORDERS, MOCK_GOODS_RECEIPTS } from '@/mocks/database';
import { env } from '@/config/env';
import { notifyGlobal } from '@/context/NotificationContext';
import {
  roundCurrency,
  calculateDaysOverdue,
} from '@/features/invoices/utils/invoiceCalculations';
import { detectDuplicateInvoice } from '@/features/invoices/utils/duplicateDetection';
import { executeThreeWayMatch } from '@/features/invoices/utils/threeWayMatching';
import { evaluatePaymentReadiness } from '@/features/invoices/utils/paymentReadiness';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const GENERATED_INVOICE_RECORDS: InvoiceRecord[] = Array.from({ length: 510 }, (_, i) => {
  const invNum = 7001 + i;
  const id = `inv-${invNum}`;
  const invoiceNumber = `INV-${invNum}`;
  const po = MOCK_PURCHASE_ORDERS[i % MOCK_PURCHASE_ORDERS.length]!;
  const isFirst = i === 0 || invoiceNumber === 'INV-7001';
  const isSecond = i === 1 || invoiceNumber === 'INV-7002';
  const isThird = i === 2 || invoiceNumber === 'INV-7003';
  const isFourth = i === 3 || invoiceNumber === 'INV-7004';
  const isFifth = i === 4 || invoiceNumber === 'INV-7005';
  const isSixth = i === 5 || invoiceNumber === 'INV-7006';

  const grn = isFourth ? null : isFirst || isSecond || isThird || isFifth || isSixth ? MOCK_GOODS_RECEIPTS[i % MOCK_GOODS_RECEIPTS.length]! : i % 12 === 0 ? null : MOCK_GOODS_RECEIPTS[i % MOCK_GOODS_RECEIPTS.length]!;

  let vendorInvoiceNumber = `VND-INV-${10000 + i}`;
  if (isFifth) {
    vendorInvoiceNumber = 'VND-INV-10001'; // Duplicate with INV-7001 for same vendor
  }

  // Generate line items
  const items: InvoiceItemRecord[] = po.items.map((it, idx) => {
    let billedQty = it.quantity;
    let billedPrice = it.unitPrice;
    let grnAcceptedQty = it.quantity;

    if (isFirst) {
      billedQty = 10;
      billedPrice = it.unitPrice;
      grnAcceptedQty = 10;
    } else if (isSecond) {
      // Quantity variance: Billed 2, but only 1 accepted in GRN (1 rejected)
      billedQty = 2;
      grnAcceptedQty = 1;
    } else if (isThird) {
      // Price variance: Billed price is $50 higher than PO price
      billedPrice = it.unitPrice + 50;
    } else if (isFourth) {
      // Missing GRN
      grnAcceptedQty = 0;
    } else if (i % 7 === 0) {
      // Periodic quantity variance
      billedQty = it.quantity;
      grnAcceptedQty = Math.max(1, it.quantity - 2);
    } else if (i % 11 === 0) {
      // Periodic price variance
      billedPrice = it.unitPrice * 1.08;
    }

    const varianceQuantity = billedQty - grnAcceptedQty;
    const priceDiff = billedPrice - it.unitPrice;
    const varianceAmount = roundCurrency(priceDiff * billedQty);

    let matchingStatus: InvoiceItemRecord['matchingStatus'] = 'MATCHED';
    if (!grn) {
      matchingStatus = 'MISSING_GRN';
    } else if (varianceQuantity > 0) {
      matchingStatus = billedQty > it.quantity ? 'OVERBILLING' : 'QUANTITY_VARIANCE';
    } else if (priceDiff > 0.01) {
      matchingStatus = 'PRICE_VARIANCE';
    }

    const taxRate = (it as any).taxPercent || 18;
    const taxAmount = roundCurrency(billedQty * billedPrice * (taxRate / 100));
    const lineTotal = roundCurrency(billedQty * billedPrice + taxAmount);

    return {
      id: `inv-item-${id}-${idx + 1}`,
      invoiceId: id,
      poItemId: it.id,
      lineNumber: idx + 1,
      materialCode: it.materialCode,
      description: it.materialName,
      quantity: billedQty,
      poQuantity: it.quantity,
      grnQuantity: grnAcceptedQty,
      uom: it.unitOfMeasure,
      unitPrice: billedPrice,
      poUnitPrice: it.unitPrice,
      taxRate,
      taxAmount,
      discount: 0,
      lineTotal,
      grnReference: grn?.grnNumber,
      matchingStatus,
      varianceQuantity,
      varianceAmount,
    };
  });

  const subtotal = roundCurrency(items.reduce((sum, it) => sum + it.quantity * it.unitPrice, 0));
  const taxAmount = roundCurrency(items.reduce((sum, it) => sum + it.taxAmount, 0));
  const totalAmount = roundCurrency(subtotal + taxAmount);

  // Exceptions list
  const exceptions: InvoiceException[] = [];
  const now = new Date().toISOString();

  if (isSecond) {
    exceptions.push({
      id: `ex-${id}-qty`,
      invoiceId: id,
      code: 'QUANTITY_VARIANCE',
      severity: 'WARNING',
      status: 'OPEN',
      description: 'Billed quantity (2) exceeds warehouse accepted quantity (1). 1 unit rejected during receiving QA.',
      expectedValue: 1,
      actualValue: 2,
      detectedAt: now,
    });
  } else if (isThird) {
    exceptions.push({
      id: `ex-${id}-price`,
      invoiceId: id,
      code: 'PRICE_VARIANCE',
      severity: 'BLOCKING',
      status: 'OPEN',
      description: 'Billed unit price exceeds PO contract price by $50 per unit.',
      expectedValue: po.items[0]?.unitPrice || 100,
      actualValue: (po.items[0]?.unitPrice || 100) + 50,
      detectedAt: now,
    });
  } else if (isFourth || !grn) {
    exceptions.push({
      id: `ex-${id}-grn`,
      invoiceId: id,
      code: 'MISSING_GRN',
      severity: 'BLOCKING',
      status: 'OPEN',
      description: `Physical goods receipt evidence missing for PO #${po.poNumber}.`,
      expectedValue: 'Warehouse GRN Voucher',
      actualValue: 'No GRN Received',
      detectedAt: now,
    });
  } else if (isFifth) {
    exceptions.push({
      id: `ex-${id}-dup`,
      invoiceId: id,
      code: 'DUPLICATE_INVOICE',
      severity: 'BLOCKING',
      status: 'OPEN',
      description: `Exact duplicate detected: Vendor invoice #VND-INV-10001 already received under INV-7001.`,
      expectedValue: 'Unique Vendor Invoice #',
      actualValue: 'VND-INV-10001',
      detectedAt: now,
    });
  }

  // Derive Statuses
  let status: InvoiceRecord['status'] = 'APPROVED';
  let matchingStatus: InvoiceRecord['matchingStatus'] = 'MATCHED';
  let paymentStatus: InvoiceRecord['paymentStatus'] = 'READY_FOR_PAYMENT';

  if (isFirst) {
    status = 'APPROVED';
    matchingStatus = 'MATCHED';
    paymentStatus = 'READY_FOR_PAYMENT';
  } else if (isSecond) {
    status = 'EXCEPTION';
    matchingStatus = 'QUANTITY_VARIANCE';
    paymentStatus = 'NOT_READY';
  } else if (isThird) {
    status = 'EXCEPTION';
    matchingStatus = 'PRICE_VARIANCE';
    paymentStatus = 'NOT_READY';
  } else if (isFourth || !grn) {
    status = 'EXCEPTION';
    matchingStatus = 'MISSING_GRN';
    paymentStatus = 'NOT_READY';
  } else if (isFifth) {
    status = 'EXCEPTION';
    matchingStatus = 'EXCEPTION';
    paymentStatus = 'NOT_READY';
  } else if (i % 8 === 0) {
    status = 'PAID';
    paymentStatus = 'PAID';
  } else if (i % 5 === 0) {
    status = 'PENDING_APPROVAL';
    paymentStatus = 'NOT_READY';
  }

  const dueDate = isSixth
    ? new Date(Date.now() - 14 * 86400000).toISOString().slice(0, 10) // 14 days overdue
    : new Date(Date.now() + ((i % 30) + 5) * 86400000).toISOString().slice(0, 10);

  const { isOverdue, daysOverdue } = calculateDaysOverdue(dueDate, paymentStatus);

  const duplicateRisk = isFifth ? 'DUPLICATE_CONFIRMED' : 'NO_DUPLICATE';

  const sapSyncStatus: InvoiceRecord['sapSyncStatus'] =
    isThird || isFifth ? 'SYNC_FAILED' : isFourth ? 'NOT_SYNCED' : i % 9 === 0 ? 'SYNC_PENDING' : 'SYNCED';

  const isReadyForPayment = paymentStatus === 'READY_FOR_PAYMENT';

  return {
    id,
    invoiceNumber,
    vendorInvoiceNumber,
    vendorId: po.vendorId,
    vendorName: po.vendorName,
    vendorCode: `VND-${po.vendorId.toUpperCase()}`,
    poId: po.id,
    poNumber: po.poNumber,
    sapPoNumber: po.sapPoNumber || `450099${80 + (i % 20)}`,
    grnId: grn?.id,
    grnNumber: grn?.grnNumber,
    sapGrnNumber: grn?.sapGrnNumber || `500090${6001 + (i % 500)}`,
    companyCode: '1000',
    plantCode: (po as any).plant || po.items[0]?.plantCode || 'PLANT-1000',
    currency: po.currency || 'INR',
    invoiceDate: new Date(Date.now() - (i % 20) * 86400000).toISOString().slice(0, 10),
    receivedDate: new Date(Date.now() - (i % 15) * 86400000).toISOString().slice(0, 10),
    dueDate,
    status,
    matchingStatus,
    paymentStatus,
    subtotal,
    taxAmount,
    taxPercent: 18,
    discountAmount: 0,
    shippingAmount: 0,
    otherCharges: 0,
    totalAmount,
    paymentTerms: po.paymentTerms || 'Net 30 Days',
    source: i % 3 === 0 ? 'PORTAL' : 'SAP_VIM',
    sapSyncStatus,
    sapMiroNumber: `510090${invNum}`,
    isDuplicate: isFifth,
    duplicateRisk,
    duplicateDetails: isFifth
      ? {
          originalInvoiceId: 'inv-7001',
          originalInvoiceNumber: 'INV-7001',
          reason: 'Exact match on Vendor + Vendor Invoice #',
        }
      : undefined,
    exceptions,
    items,
    isOverdue,
    daysOverdue,
    isReadyForPayment,
    documentsCount: (i % 3) + 1,
    commentsCount: i % 4,
    auditHistory: [
      {
        id: `aud-${id}-created`,
        invoiceId: id,
        timestamp: new Date(Date.now() - 86400000 * 3).toISOString(),
        eventType: 'INVOICE_CREATED',
        actorId: 'usr-fin-01',
        actorName: 'Sarah Jenkins',
        actorRole: 'Accounts Payable Clerk',
        details: `Invoice ingested against PO #${po.poNumber}.`,
        systemSource: 'ProcureAI',
      },
      {
        id: `aud-${id}-matched`,
        invoiceId: id,
        timestamp: new Date(Date.now() - 86400000 * 2).toISOString(),
        eventType: 'MATCH_COMPLETED',
        actorId: 'System',
        actorName: '3-Way Match Engine',
        actorRole: 'Automated Bot',
        details: `3-Way match evaluated: Status ${matchingStatus}.`,
        systemSource: 'ProcureAI',
      },
      ...(sapSyncStatus === 'SYNCED'
        ? [
            {
              id: `aud-${id}-sap`,
              invoiceId: id,
              timestamp: new Date(Date.now() - 86400000).toISOString(),
              eventType: 'SAP_SYNC_SUCCESS' as const,
              actorId: 'SAP_BAPI',
              actorName: 'SAP MIRO Service',
              actorRole: 'Integration Service',
              details: `Posted to SAP S/4HANA MIRO document #510090${invNum}.`,
              systemSource: 'SAP_MIRO' as const,
            },
          ]
        : []),
    ],
    notes: 'Accounts Payable standard ingestion and verification.',
    version: 1,
    createdAt: new Date(Date.now() - (i % 25) * 86400000).toISOString(),
    updatedAt: new Date().toISOString(),
  };
});

class MockInvoiceService {
  private invoices: InvoiceRecord[] = [...GENERATED_INVOICE_RECORDS];
  private idempotencyCache = new Map<string, InvoiceRecord>();

  private findInternalRecord(id: string): InvoiceRecord | undefined {
    return this.invoices.find((inv) => inv.id === id || inv.invoiceNumber === id);
  }

  syncInvoiceRecord(inv: InvoiceRecord, previousId?: string): void {
    const idx = this.invoices.findIndex(
      (i) => i.id === inv.id || i.invoiceNumber === inv.invoiceNumber || (previousId && i.id === previousId)
    );
    if (idx >= 0) {
      this.invoices[idx] = { ...this.invoices[idx], ...inv };
    } else {
      this.invoices.unshift(inv);
    }
  }

  async getInvoices(
    filters: InvoiceFilterState
  ): Promise<{ items: InvoiceRecord[]; total: number; page: number; pageSize: number }> {
    await delay(env.mockDelayMs);
    let results = [...this.invoices];

    if (filters.search) {
      const q = filters.search.toLowerCase().trim();
      results = results.filter(
        (inv) =>
          inv.invoiceNumber.toLowerCase().includes(q) ||
          inv.vendorInvoiceNumber.toLowerCase().includes(q) ||
          inv.poNumber.toLowerCase().includes(q) ||
          (inv.grnNumber && inv.grnNumber.toLowerCase().includes(q)) ||
          inv.vendorName.toLowerCase().includes(q) ||
          (inv.sapMiroNumber && inv.sapMiroNumber.toLowerCase().includes(q)) ||
          inv.items.some((it) => it.materialCode.toLowerCase().includes(q) || it.description.toLowerCase().includes(q))
      );
    }

    if (filters.status && filters.status !== 'ALL') {
      results = results.filter((inv) => inv.status === filters.status);
    }

    if (filters.matchingStatus && filters.matchingStatus !== 'ALL') {
      results = results.filter((inv) => inv.matchingStatus === filters.matchingStatus);
    }

    if (filters.paymentStatus && filters.paymentStatus !== 'ALL') {
      results = results.filter((inv) => inv.paymentStatus === filters.paymentStatus);
    }

    if (filters.exceptionCode && filters.exceptionCode !== 'ALL') {
      results = results.filter((inv) => inv.exceptions.some((e) => e.code === filters.exceptionCode));
    }

    if (filters.vendor && filters.vendor !== 'ALL') {
      results = results.filter((inv) => inv.vendorId === filters.vendor || inv.vendorCode === filters.vendor);
    }

    if (filters.plant && filters.plant !== 'ALL') {
      results = results.filter((inv) => inv.plantCode === filters.plant);
    }

    if (filters.isOverdue !== undefined) {
      results = results.filter((inv) => inv.isOverdue === filters.isOverdue);
    }

    // Sort
    const sortBy = filters.sortBy || 'invoiceDate';
    const sortOrder = filters.sortOrder || 'desc';
    results.sort((a, b) => {
      let valA: string | number = a[sortBy];
      let valB: string | number = b[sortBy];
      if (sortBy === 'invoiceDate' || sortBy === 'dueDate') {
        valA = new Date(valA as string).getTime();
        valB = new Date(valB as string).getTime();
      }
      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    const page = filters.page || 1;
    const pageSize = filters.pageSize || 25;
    const startIndex = (page - 1) * pageSize;
    const paginatedItems = results.slice(startIndex, startIndex + pageSize);

    return {
      items: JSON.parse(JSON.stringify(paginatedItems)),
      total: results.length,
      page,
      pageSize,
    };
  }

  async getInvoiceById(id: string): Promise<InvoiceRecord | null> {
    await delay(env.mockDelayMs);
    const inv = this.findInternalRecord(id);
    if (!inv) return null;
    return JSON.parse(JSON.stringify(inv));
  }

  async getInvoiceMetrics(): Promise<InvoiceMetricsSummary> {
    await delay(env.mockDelayMs);
    const totalInvoices = this.invoices.length;
    let pendingValidation = 0;
    let pendingMatching = 0;
    let matchedCount = 0;
    let exceptionCount = 0;
    let duplicateRiskCount = 0;
    let missingGrnCount = 0;
    let quantityVarianceCount = 0;
    let priceVarianceCount = 0;
    let overdueCount = 0;
    let dueSoonCount = 0;
    let readyForPaymentCount = 0;
    let paidCount = 0;
    let totalInvoiceValue = 0;
    let readyForPaymentValue = 0;
    let overdueValue = 0;

    const now = Date.now();

    this.invoices.forEach((inv) => {
      totalInvoiceValue += inv.totalAmount;

      if (inv.status === 'UNDER_VALIDATION' || inv.status === 'DRAFT') pendingValidation++;
      if (inv.status === 'PENDING_MATCH') pendingMatching++;
      if (inv.matchingStatus === 'MATCHED') matchedCount++;
      if (inv.exceptions.some((e) => e.status !== 'RESOLVED')) exceptionCount++;
      if (inv.duplicateRisk !== 'NO_DUPLICATE') duplicateRiskCount++;
      if (inv.matchingStatus === 'MISSING_GRN') missingGrnCount++;
      if (inv.matchingStatus === 'QUANTITY_VARIANCE') quantityVarianceCount++;
      if (inv.matchingStatus === 'PRICE_VARIANCE') priceVarianceCount++;

      if (inv.isOverdue) {
        overdueCount++;
        overdueValue += inv.totalAmount;
      } else if (inv.dueDate) {
        const daysToDue = Math.ceil((new Date(inv.dueDate).getTime() - now) / 86400000);
        if (daysToDue <= 5 && daysToDue >= 0) dueSoonCount++;
      }

      if (inv.paymentStatus === 'READY_FOR_PAYMENT') {
        readyForPaymentCount++;
        readyForPaymentValue += inv.totalAmount;
      }
      if (inv.paymentStatus === 'PAID') paidCount++;
    });

    return {
      totalInvoices,
      pendingValidation,
      pendingMatching,
      matchedCount,
      exceptionCount,
      duplicateRiskCount,
      missingGrnCount,
      quantityVarianceCount,
      priceVarianceCount,
      overdueCount,
      dueSoonCount,
      readyForPaymentCount,
      paidCount,
      totalInvoiceValue: roundCurrency(totalInvoiceValue),
      readyForPaymentValue: roundCurrency(readyForPaymentValue),
      overdueValue: roundCurrency(overdueValue),
    };
  }

  async createInvoice(input: CreateInvoiceInput, idempotencyKey?: string): Promise<InvoiceRecord> {
    await delay(env.mockDelayMs);

    if (idempotencyKey && this.idempotencyCache.has(idempotencyKey)) {
      return JSON.parse(JSON.stringify(this.idempotencyCache.get(idempotencyKey)!));
    }

    // Duplicate detection check
    const dupCheck = detectDuplicateInvoice(
      {
        vendorId: input.vendorId,
        vendorInvoiceNumber: input.vendorInvoiceNumber,
        totalAmount: 0,
        poId: input.poId,
        poNumber: input.poNumber,
      },
      this.invoices
    );

    const nextInvNum = 7001 + this.invoices.length;
    const nextId = `inv-${nextInvNum}`;
    const nextInvoiceNumber = `INV-${nextInvNum}`;

    const items: InvoiceItemRecord[] = input.items.map((it, idx) => {
      const taxAmount = roundCurrency(it.quantity * it.unitPrice * (it.taxRate / 100));
      const lineTotal = roundCurrency(it.quantity * it.unitPrice + taxAmount);

      return {
        id: `inv-item-${nextId}-${idx + 1}`,
        invoiceId: nextId,
        poItemId: it.poItemId,
        lineNumber: it.lineNumber,
        materialCode: it.materialCode,
        description: it.description,
        quantity: it.quantity,
        poQuantity: it.quantity,
        grnQuantity: it.quantity,
        uom: it.uom,
        unitPrice: it.unitPrice,
        poUnitPrice: it.unitPrice,
        taxRate: it.taxRate,
        taxAmount,
        discount: it.discount || 0,
        lineTotal,
        grnReference: input.grnNumber,
        matchingStatus: 'MATCHED',
        varianceQuantity: 0,
        varianceAmount: 0,
      };
    });

    const subtotal = roundCurrency(items.reduce((s, it) => s + it.quantity * it.unitPrice, 0));
    const taxAmount = roundCurrency(items.reduce((s, it) => s + it.taxAmount, 0));
    const shipping = roundCurrency(input.shippingAmount || 0);
    const other = roundCurrency(input.otherCharges || 0);
    const discount = roundCurrency(input.discountAmount || 0);
    const grandTotal = roundCurrency(subtotal + taxAmount + shipping + other - discount);

    const exceptions: InvoiceException[] = [];
    if (dupCheck.isDuplicate) {
      exceptions.push({
        id: `ex-${nextId}-dup`,
        invoiceId: nextId,
        code: 'DUPLICATE_INVOICE',
        severity: 'BLOCKING',
        status: 'OPEN',
        description: dupCheck.reason || 'Duplicate invoice detected.',
        expectedValue: 'Unique Vendor Invoice #',
        actualValue: input.vendorInvoiceNumber,
        detectedAt: new Date().toISOString(),
      });
    }

    const { isOverdue, daysOverdue } = calculateDaysOverdue(input.dueDate, 'NOT_READY');

    const newInvoice: InvoiceRecord = {
      id: nextId,
      invoiceNumber: nextInvoiceNumber,
      vendorInvoiceNumber: input.vendorInvoiceNumber,
      vendorId: input.vendorId,
      vendorName: input.vendorName,
      vendorCode: input.vendorCode,
      poId: input.poId,
      poNumber: input.poNumber,
      grnId: input.grnId,
      grnNumber: input.grnNumber,
      companyCode: input.companyCode || '1000',
      plantCode: input.plantCode || 'PLANT-1000',
      currency: input.currency,
      invoiceDate: input.invoiceDate,
      receivedDate: input.receivedDate,
      dueDate: input.dueDate,
      status: exceptions.length > 0 ? 'EXCEPTION' : 'MATCHED',
      matchingStatus: exceptions.length > 0 ? 'EXCEPTION' : 'MATCHED',
      paymentStatus: 'NOT_READY',
      subtotal,
      taxAmount,
      taxPercent: 18,
      discountAmount: discount,
      shippingAmount: shipping,
      otherCharges: other,
      totalAmount: grandTotal,
      paymentTerms: input.paymentTerms || 'Net 30 Days',
      source: 'PORTAL',
      sapSyncStatus: 'NOT_SYNCED',
      isDuplicate: dupCheck.isDuplicate,
      duplicateRisk: dupCheck.duplicateRisk,
      duplicateDetails: dupCheck.isDuplicate
        ? {
            originalInvoiceId: dupCheck.originalInvoiceId,
            originalInvoiceNumber: dupCheck.originalInvoiceNumber,
            reason: dupCheck.reason,
          }
        : undefined,
      exceptions,
      items,
      isOverdue,
      daysOverdue,
      isReadyForPayment: false,
      documentsCount: 0,
      commentsCount: 0,
      auditHistory: [
        {
          id: `aud-${nextId}-created`,
          invoiceId: nextId,
          timestamp: new Date().toISOString(),
          eventType: 'INVOICE_CREATED',
          actorId: 'usr-fin-01',
          actorName: 'Sarah Jenkins',
          actorRole: 'Accounts Payable Clerk',
          details: `Invoice created against PO #${input.poNumber}. Total: ${grandTotal} ${input.currency}.`,
          systemSource: 'ProcureAI',
        },
      ],
      notes: input.notes,
      version: 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.invoices.unshift(newInvoice);
    if (idempotencyKey) {
      this.idempotencyCache.set(idempotencyKey, newInvoice);
    }

    notifyGlobal('Invoice Ingested', `Invoice ${newInvoice.invoiceNumber} recorded for ${newInvoice.vendorName}.`, 'System');

    return JSON.parse(JSON.stringify(newInvoice));
  }

  async runThreeWayMatch(id: string): Promise<InvoiceRecord> {
    await delay(env.mockDelayMs);
    const inv = this.findInternalRecord(id);
    if (!inv) throw new Error('Invoice not found');

    const po = MOCK_PURCHASE_ORDERS.find((p) => p.id === inv.poId || p.poNumber === inv.poNumber);
    const grn = MOCK_GOODS_RECEIPTS.find((g) => g.id === inv.grnId || g.grnNumber === inv.grnNumber);

    const matchResult = executeThreeWayMatch({
      invoiceId: inv.id,
      invoiceNumber: inv.invoiceNumber,
      vendorId: inv.vendorId,
      vendorName: inv.vendorName,
      currency: inv.currency,
      subtotal: inv.subtotal,
      taxAmount: inv.taxAmount,
      shippingAmount: inv.shippingAmount,
      otherCharges: inv.otherCharges,
      discountAmount: inv.discountAmount,
      totalAmount: inv.totalAmount,
      po: po as any,
      grn: grn as any,
      rawItems: inv.items.map((it) => ({
        poItemId: it.poItemId,
        lineNumber: it.lineNumber,
        materialCode: it.materialCode,
        description: it.description,
        quantity: it.quantity,
        uom: it.uom,
        unitPrice: it.unitPrice,
        taxRate: it.taxRate,
        discount: it.discount,
      })),
    });

    inv.matchingStatus = matchResult.matchingStatus;
    inv.items = matchResult.evaluatedItems;

    // Merge exceptions
    matchResult.exceptions.forEach((newEx) => {
      const exists = inv.exceptions.some((e) => e.code === newEx.code && e.status !== 'RESOLVED');
      if (!exists) {
        inv.exceptions.push(newEx);
      }
    });

    if (inv.matchingStatus === 'MATCHED' && !inv.exceptions.some((e) => e.status !== 'RESOLVED')) {
      inv.status = 'MATCHED';
    } else {
      inv.status = 'EXCEPTION';
    }

    const readiness = evaluatePaymentReadiness(inv);
    inv.isReadyForPayment = readiness.isReadyForPayment;
    inv.version += 1;
    inv.updatedAt = new Date().toISOString();

    inv.auditHistory.unshift({
      id: `aud-${inv.id}-${Date.now()}`,
      invoiceId: inv.id,
      timestamp: inv.updatedAt,
      eventType: 'MATCH_COMPLETED',
      actorId: 'System',
      actorName: '3-Way Match Engine',
      actorRole: 'Automated Bot',
      details: `3-Way match executed. Result: ${inv.matchingStatus}. Exceptions: ${inv.exceptions.length}.`,
      systemSource: 'ProcureAI',
    });

    notifyGlobal('3-Way Match Completed', `Invoice ${inv.invoiceNumber} match evaluated as ${inv.matchingStatus}.`, 'System');

    return JSON.parse(JSON.stringify(inv));
  }

  async resolveException(
    invoiceId: string,
    exceptionId: string,
    resolutionNotes: string
  ): Promise<InvoiceRecord> {
    await delay(env.mockDelayMs);
    const inv = this.findInternalRecord(invoiceId);
    if (!inv) throw new Error('Invoice not found');

    const ex = inv.exceptions.find((e) => e.id === exceptionId);
    if (!ex) throw new Error('Exception not found');

    ex.status = 'RESOLVED';
    ex.resolvedBy = 'Sarah Jenkins (Finance Lead)';
    ex.resolvedAt = new Date().toISOString();
    ex.resolutionNotes = resolutionNotes;

    // Check if remaining active exceptions exist
    const hasRemainingExceptions = inv.exceptions.some((e) => e.status !== 'RESOLVED');
    if (!hasRemainingExceptions) {
      inv.status = 'MATCHED';
      inv.matchingStatus = 'MATCHED';
    }

    const readiness = evaluatePaymentReadiness(inv);
    inv.isReadyForPayment = readiness.isReadyForPayment;
    inv.version += 1;
    inv.updatedAt = new Date().toISOString();

    inv.auditHistory.unshift({
      id: `aud-${inv.id}-${Date.now()}`,
      invoiceId: inv.id,
      timestamp: inv.updatedAt,
      eventType: 'EXCEPTION_RESOLVED',
      actorId: 'usr-fin-01',
      actorName: 'Sarah Jenkins',
      actorRole: 'Finance Lead',
      details: `Exception ${ex.code} resolved. Justification: ${resolutionNotes}`,
      systemSource: 'ProcureAI',
    });

    notifyGlobal('Exception Resolved', `Exception ${ex.code} cleared on invoice ${inv.invoiceNumber}.`, 'System');

    return JSON.parse(JSON.stringify(inv));
  }

  async approveInvoice(id: string, notes?: string): Promise<InvoiceRecord> {
    await delay(env.mockDelayMs);
    const inv = this.findInternalRecord(id);
    if (!inv) throw new Error('Invoice not found');

    const hasUnresolved = inv.exceptions.some((e) => e.status !== 'RESOLVED' && e.severity === 'BLOCKING');
    if (hasUnresolved) {
      throw new Error('Cannot approve invoice with active blocking exceptions.');
    }

    inv.status = 'APPROVED';
    const readiness = evaluatePaymentReadiness(inv);
    inv.isReadyForPayment = readiness.isReadyForPayment;
    inv.version += 1;
    inv.updatedAt = new Date().toISOString();

    inv.auditHistory.unshift({
      id: `aud-${inv.id}-${Date.now()}`,
      invoiceId: inv.id,
      timestamp: inv.updatedAt,
      eventType: 'INVOICE_APPROVED',
      actorId: 'usr-fin-01',
      actorName: 'Sarah Jenkins',
      actorRole: 'Finance Manager',
      details: `Invoice approved for payment. ${notes || ''}`,
      systemSource: 'ProcureAI',
    });

    notifyGlobal('Invoice Approved', `Invoice ${inv.invoiceNumber} approved by Accounts Payable.`, 'System');

    return JSON.parse(JSON.stringify(inv));
  }

  async rejectInvoice(id: string, reason: string): Promise<InvoiceRecord> {
    await delay(env.mockDelayMs);
    const inv = this.findInternalRecord(id);
    if (!inv) throw new Error('Invoice not found');

    inv.status = 'REJECTED';
    inv.paymentStatus = 'NOT_READY';
    inv.isReadyForPayment = false;
    inv.version += 1;
    inv.updatedAt = new Date().toISOString();

    inv.auditHistory.unshift({
      id: `aud-${inv.id}-${Date.now()}`,
      invoiceId: inv.id,
      timestamp: inv.updatedAt,
      eventType: 'INVOICE_REJECTED',
      actorId: 'usr-fin-01',
      actorName: 'Sarah Jenkins',
      actorRole: 'Finance Manager',
      details: `Invoice rejected. Reason: ${reason}`,
      systemSource: 'ProcureAI',
    });

    notifyGlobal('Invoice Rejected', `Invoice ${inv.invoiceNumber} rejected by Finance.`, 'System');

    return JSON.parse(JSON.stringify(inv));
  }

  async markReadyForPayment(id: string): Promise<InvoiceRecord> {
    await delay(env.mockDelayMs);
    const inv = this.findInternalRecord(id);
    if (!inv) throw new Error('Invoice not found');

    const readiness = evaluatePaymentReadiness(inv);
    if (!readiness.isReadyForPayment) {
      throw new Error(`Cannot authorize payment: ${readiness.blockingReasons.join('; ')}`);
    }

    inv.status = 'READY_FOR_PAYMENT';
    inv.paymentStatus = 'READY_FOR_PAYMENT';
    inv.isReadyForPayment = true;
    inv.version += 1;
    inv.updatedAt = new Date().toISOString();

    inv.auditHistory.unshift({
      id: `aud-${inv.id}-${Date.now()}`,
      invoiceId: inv.id,
      timestamp: inv.updatedAt,
      eventType: 'PAYMENT_READINESS_CHANGED',
      actorId: 'usr-fin-01',
      actorName: 'Sarah Jenkins',
      actorRole: 'Finance Lead',
      details: 'Authorized for payment disbursement in downstream payment run.',
      systemSource: 'ProcureAI',
    });

    notifyGlobal('Payment Authorized', `Invoice ${inv.invoiceNumber} marked Ready for Payment.`, 'System');

    return JSON.parse(JSON.stringify(inv));
  }

  async placeOnHold(id: string, reason: string): Promise<InvoiceRecord> {
    await delay(env.mockDelayMs);
    const inv = this.findInternalRecord(id);
    if (!inv) throw new Error('Invoice not found');

    inv.status = 'ON_HOLD';
    inv.paymentStatus = 'NOT_READY';
    inv.isReadyForPayment = false;
    inv.version += 1;
    inv.updatedAt = new Date().toISOString();

    inv.auditHistory.unshift({
      id: `aud-${inv.id}-${Date.now()}`,
      invoiceId: inv.id,
      timestamp: inv.updatedAt,
      eventType: 'INVOICE_HELD',
      actorId: 'usr-fin-01',
      actorName: 'Sarah Jenkins',
      actorRole: 'Finance Lead',
      details: `Invoice placed on hold. Reason: ${reason}`,
      systemSource: 'ProcureAI',
    });

    return JSON.parse(JSON.stringify(inv));
  }

  async releaseFromHold(id: string, notes?: string): Promise<InvoiceRecord> {
    await delay(env.mockDelayMs);
    const inv = this.findInternalRecord(id);
    if (!inv) throw new Error('Invoice not found');

    inv.status = 'UNDER_VALIDATION';
    inv.version += 1;
    inv.updatedAt = new Date().toISOString();

    inv.auditHistory.unshift({
      id: `aud-${inv.id}-${Date.now()}`,
      invoiceId: inv.id,
      timestamp: inv.updatedAt,
      eventType: 'INVOICE_RELEASED',
      actorId: 'usr-fin-01',
      actorName: 'Sarah Jenkins',
      actorRole: 'Finance Lead',
      details: `Released from hold. ${notes || ''}`,
      systemSource: 'ProcureAI',
    });

    return JSON.parse(JSON.stringify(inv));
  }

  async syncWithSapMiro(id: string): Promise<{ success: boolean; sapMiroNumber: string; invoice: InvoiceRecord }> {
    await delay(env.mockDelayMs);
    const inv = this.findInternalRecord(id);
    if (!inv) throw new Error('Invoice not found');

    inv.sapSyncStatus = 'SYNCED';
    inv.sapMiroNumber = inv.sapMiroNumber || `510090${Math.floor(1000 + Math.random() * 9000)}`;
    inv.updatedAt = new Date().toISOString();

    inv.auditHistory.unshift({
      id: `aud-${inv.id}-${Date.now()}`,
      invoiceId: inv.id,
      timestamp: inv.updatedAt,
      eventType: 'SAP_SYNC_SUCCESS',
      actorId: 'SAP_MIRO',
      actorName: 'SAP MIRO BAPI',
      actorRole: 'Integration Service',
      details: `Replicated invoice to SAP S/4HANA MIRO document #${inv.sapMiroNumber} via BAPI_INCOMINGINVOICE_CREATE.`,
      systemSource: 'SAP_MIRO',
    });

    notifyGlobal('SAP MIRO Synced', `Invoice ${inv.invoiceNumber} posted to SAP MIRO (${inv.sapMiroNumber}).`, 'System');

    return {
      success: true,
      sapMiroNumber: inv.sapMiroNumber,
      invoice: JSON.parse(JSON.stringify(inv)),
    };
  }

  async cancelInvoice(id: string, reason?: string): Promise<InvoiceRecord> {
    await delay(env.mockDelayMs);
    const inv = this.findInternalRecord(id);
    if (!inv) throw new Error('Invoice not found');

    inv.status = 'CANCELLED';
    inv.paymentStatus = 'CANCELLED';
    inv.isReadyForPayment = false;
    inv.updatedAt = new Date().toISOString();

    inv.auditHistory.unshift({
      id: `aud-${inv.id}-${Date.now()}`,
      invoiceId: inv.id,
      timestamp: inv.updatedAt,
      eventType: 'INVOICE_CANCELLED',
      actorId: 'usr-fin-01',
      actorName: 'Sarah Jenkins',
      actorRole: 'Finance Lead',
      details: `Invoice cancelled. Reason: ${reason || 'Administrative cancellation'}`,
      systemSource: 'ProcureAI',
    });

    return JSON.parse(JSON.stringify(inv));
  }

  async updateInvoice(id: string, data: Partial<InvoiceRecord>): Promise<InvoiceRecord> {
    await delay(env.mockDelayMs);
    const inv = this.findInternalRecord(id);
    if (!inv) throw new Error('Invoice not found');
    Object.assign(inv, data);
    inv.updatedAt = new Date().toISOString();
    return JSON.parse(JSON.stringify(inv));
  }

  async deleteInvoice(id: string): Promise<boolean> {
    await delay(env.mockDelayMs);
    const initialLen = this.invoices.length;
    this.invoices = this.invoices.filter((i) => i.id !== id && i.invoiceNumber !== id);
    return this.invoices.length < initialLen;
  }
}

export const mockInvoiceService = new MockInvoiceService();
